import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { CommonModule } from '@angular/common';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { HttpEventType } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { FileManagerActions } from '../file-manager-state/file-manager.actions';
import { FileManagerService } from '../file-manager.service';
import { FileManagerState } from '../file-manager-state/file-manager.state';
import { GridDocumentModel, UploadedFile } from '../file-manager-state/file-manager.model';
import { Subscription } from 'rxjs';
// import { switchMap } from 'rxjs';

interface FileData {
  file: File;
  progress: number; // -1 for not started, 0-100 for in-progress, 100 for completed
  uploaded: boolean; // Indicates if the file has been uploaded
}

@Component({
  selector: 'app-file-upload',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule, CommonModule, MatProgressBarModule],
  templateUrl: './file-upload.component.html',
  styleUrl: './file-upload.component.scss'
})
export class FileUploadComponent implements OnInit, OnDestroy {

  private _store = inject(Store);
  private _fileManagerService = inject(FileManagerService);
  private _drawerStateSubscription: Subscription;

  isDragOver = false;
  files: FileData[] = [];

  ngOnInit(): void {
    // Subscribe to drawer state changes to detect when drawer is closed
    this._drawerStateSubscription = this._store.select(FileManagerState.getFileUploadDrawerState)
      .subscribe(isOpen => {
        if (!isOpen && this.files.length > 0) {
          // Clear files when drawer is closed
          this.files = [];
        }
      });
  }

  ngOnDestroy(): void {
    if (this._drawerStateSubscription) {
      this._drawerStateSubscription.unsubscribe();
    }
    this.onCloseUpload();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
    if (event.dataTransfer?.files) {
      this.addFiles(event.dataTransfer.files);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input?.files) {
      this.addFiles(input.files);
    }
  }

  private addFiles(fileList: FileList): void {
    const filesArray = Array.from(fileList);
    for (const file of filesArray) {
      this.files.push({
        file: file,
        progress: 0,
        uploaded: false,
      });
      this._store.dispatch(new FileManagerActions.OnSetUploadedFiles({
        file: file,
        progress: 0,
        uploaded: false,
      }));
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this._store.dispatch(new FileManagerActions.OnRemoveUploadedFiles(index));
  }

  uploadAllFiles(): void {
    for (const [index, file] of this.files.entries()) {
      if (!file.uploaded) this.uploadFile(index);
    }
  }

  async uploadFile(index: number) {
    // Get file data from store
    const fileData = this._store.selectSnapshot(FileManagerState.getUploadedFiles)[index];
    const formData = new FormData();
    formData.append('file', fileData.file);

    const gridDocument: GridDocumentModel = await this._store.selectSnapshot(FileManagerState.getGridDocument);

    formData.append('documentId', gridDocument._id);
    formData.append('documentReference', gridDocument.documentReference);
    formData.append('documentType', gridDocument.documentType);

    this._fileManagerService.uploadFile(formData).subscribe({
      next: (event: any) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          // Update progress in the store instead of directly modifying the object
          const progress = Math.round((event.loaded / event.total) * 100);
          const updatedFile: UploadedFile = {
            ...fileData,
            progress: progress
          };
          this._store.dispatch(new FileManagerActions.OnSetUploadedFiles(updatedFile));

          // Update local files array for UI
          this.files[index].progress = progress;
        } else if (event.type === HttpEventType.Response) {
          // Update uploaded status in the store
          const updatedFile: UploadedFile = {
            ...fileData,
            uploaded: true
          };
          this._store.dispatch(new FileManagerActions.OnSetUploadedFiles(updatedFile));

          // Update local files array for UI
          this.files[index].uploaded = true;

          this._store.dispatch(new FileManagerActions.UpdateGridDocument(gridDocument._id));

          // Fetch updated files list when upload is complete
          this._store.dispatch(new FileManagerActions.GetFiles());
          this._store.dispatch(new FileManagerActions.OnResetUploadedFiles());

        }
      },
      error: () => {
        // Update error state in the store
        const updatedFile: UploadedFile = {
          ...fileData,
          progress: 0
        };
        this._store.dispatch(new FileManagerActions.OnSetUploadedFiles(updatedFile));

        // Update local files array for UI
        this.files[index].progress = 0;
      }
    });
  }

  onCloseUpload() {
    // First clear the files array
    this.files = [];
    // Then update the store state
    this._store.dispatch(new FileManagerActions.OnResetUploadedFiles());
    this._store.dispatch(new FileManagerActions.SetFileUploadDrawerState(false));
    this._store.dispatch(new FileManagerActions.GetFiles());
  }
}